// Centralized presentation formatters. Product timezone: Europe/Istanbul.

export const TZ = "Europe/Istanbul";
const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

export function fmtOdd(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return "—";
  return v.toFixed(2);
}
export function fmtPct(v: number | null | undefined, digits = 0): string {
  if (v == null || isNaN(v)) return "—";
  return `${v.toFixed(digits)}%`;
}
export function fmtNum(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("tr-TR").format(v);
}
export function scoreLine(h: number | null | undefined, a: number | null | undefined): string {
  if (h == null || a == null) return "—";
  return `${h} – ${a}`;
}

function tzParts(iso: string) {
  const dt = new Date(iso);
  const f = new Intl.DateTimeFormat("tr-TR", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(dt);
  const g = (t: string) => f.find((p) => p.type === t)?.value || "";
  return { y: +g("year"), mo: +g("month"), d: +g("day"), hh: g("hour"), mm: g("minute"), date: dt };
}

export function clockHM(iso: string | null | undefined): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const p = tzParts(iso);
  return `${p.hh}:${p.mm}`;
}

export function dateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const p = tzParts(iso);
  return `${String(p.d).padStart(2, "0")} ${TR_MONTHS[p.mo - 1]}`;
}

// "Bugün · 21:45" / "Yarın · 19:00" / "20 Eyl · 21:45" (Europe/Istanbul).
export function kickoffLabel(iso: string | null | undefined, lang: "tr" | "en" = "tr"): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const p = tzParts(iso);
  const now = tzParts(new Date().toISOString());
  const dayDiff = Math.round(
    (Date.UTC(p.y, p.mo - 1, p.d) - Date.UTC(now.y, now.mo - 1, now.d)) / 86400000,
  );
  const time = `${p.hh}:${p.mm}`;
  if (dayDiff === 0) return `${lang === "tr" ? "Bugün" : "Today"} · ${time}`;
  if (dayDiff === 1) return `${lang === "tr" ? "Yarın" : "Tomorrow"} · ${time}`;
  if (dayDiff === -1) return `${lang === "tr" ? "Dün" : "Yesterday"} · ${time}`;
  return `${String(p.d).padStart(2, "0")} ${TR_MONTHS[p.mo - 1]} · ${time}`;
}

export function timeAgo(iso: string | null | undefined, lang: "tr" | "en" = "tr"): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return lang === "tr" ? "az önce" : "just now";
  if (m < 60) return lang === "tr" ? `${m} dk önce` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === "tr" ? `${h} sa önce` : `${h}h ago`;
  const day = Math.floor(h / 24);
  return lang === "tr" ? `${day} gün önce` : `${day}d ago`;
}

// Live trial countdown. "2 gün 23 sa 41 dk"; under an hour adds seconds.
export function fmtDuration(secs: number | null | undefined, lang: "tr" | "en" = "tr"): string {
  if (secs == null || isNaN(secs) || secs < 0) secs = 0;
  const s = Math.floor(secs);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const L = lang === "tr" ? { d: "gün", h: "sa", m: "dk", s: "sn" } : { d: "d", h: "h", m: "m", s: "s" };
  if (s < 3600) return `${m} ${L.m} ${sec} ${L.s}`;
  const parts: string[] = [];
  if (d > 0) parts.push(`${d} ${L.d}`);
  parts.push(`${h} ${L.h}`);
  parts.push(`${m} ${L.m}`);
  return parts.join(" ");
}

